/**
 * `next/image` shim wrapping @unpic/react's `<Image>`. Preserves the
 * Next prop names the legacy code uses (priority, sizes, fill,
 * placeholder, blurDataURL, loading, quality, unoptimized, onLoad).
 *
 * `fill` semantics are emulated explicitly (see below). Everything
 * else is a 1:1 passthrough.
 */
import { Image as UnpicImage } from '@unpic/react';
import type { CSSProperties, ReactEventHandler } from 'react';

export interface NextImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  priority?: boolean;
  sizes?: string;
  fill?: boolean;
  className?: string;
  style?: CSSProperties;
  loading?: 'lazy' | 'eager';
  quality?: number;
  unoptimized?: boolean;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  onLoad?: ReactEventHandler<HTMLImageElement>;
  onError?: ReactEventHandler<HTMLImageElement>;
  // Next's Image accepts the HTML-string variants of these booleans too.
  draggable?: boolean | 'true' | 'false';
  unselectable?: 'on' | 'off';
  decoding?: 'async' | 'sync' | 'auto';
  // Allow any extra attributes — Next's Image is loose-typed here.
  [key: string]: unknown;
}

// Next/Image's `fill` is documented as "image positions absolutely to
// fill the parent". @unpic's `layout="fullWidth"` is *different*: it
// sets `width: 100%` and lets `height` go to the image's intrinsic
// aspect ratio. That mismatch broke every `fill` consumer in this
// codebase silently — portrait posters in landscape cards overflowed
// vertically (then got clipped by the parent's overflow:hidden, which
// reads as "cut off"); landscape banners in tall mobile heroes left
// gaps above and below the image. `object-cover` + `object-position`
// did nothing because the img wasn't actually sized to the container.
//
// Patch: when `fill` is passed, inject the same CSS Next/Image uses —
// absolute positioning, full bleed of the nearest positioned
// ancestor. `object-fit` (via className="object-cover") and
// `object-position` (via className or style) now anchor against a
// container that actually matches the parent's dimensions.
const FILL_STYLE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
};

export default function Image({
  fill,
  width,
  height,
  priority,
  loading,
  unoptimized: _unoptimized,
  placeholder,
  blurDataURL,
  style,
  ...rest
}: NextImageProps) {
  const computedLoading = priority ? 'eager' : loading;

  // Loose-typed forward to @unpic. The Next consumers pass several
  // HTML attribute aliases (draggable as string, unselectable, decoding,
  // etc.) that don't line up with @unpic's narrower public types; the
  // runtime DOM accepts them all. Cast to bypass the strict surface.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Img = UnpicImage as any;
  return (
    <Img
      {...rest}
      width={width as number}
      height={height as number}
      layout={fill ? 'fullWidth' : 'constrained'}
      priority={priority}
      loading={computedLoading}
      background={placeholder === 'blur' ? blurDataURL || 'auto' : undefined}
      style={fill ? { ...FILL_STYLE, ...style } : style}
    />
  );
}
